import { ref } from 'vue'
import { defineStore } from 'pinia'

import { client } from '@/graphql'
import { getVarietyPacks } from '@/graphql/queries/variety-packs'
import { unpackVarietyPacks } from '@/graphql/utils/products'

import { useThemeStore } from './theme'
import { useProductsStore } from './products'
import type { VarietyPackEntry, GraphqlVarietyPack } from '@/types/shopify'

export const useVarietyPacksStore = defineStore('variety-packs', () => {
  const theme = useThemeStore()
  const products = useProductsStore()
  const loading = ref(false)
  const varietyPacks = ref<GraphqlVarietyPack[]>([])
  const varietyPack = ref<GraphqlVarietyPack>()

  const load = async () => {
    loading.value = true
    const { data } = await client.query<any>({
      query: getVarietyPacks,
      variables: {
        handle: 'active-variety-packs',
        locale: theme.locale.toUpperCase()
      }
    })
    varietyPacks.value = unpackVarietyPacks(data.collection)
    loading.value = false
  }

  const contents = (pack?: GraphqlVarietyPack, size?: number) => {
    if (pack && size) {
      const { productList } = products
      const { flavors, skuMap, quantityMap } = pack
      const key = String(size)
      if (!flavors?.length) {
        return []
      }

      // New sku map implementation
      if (skuMap?.[key]) {
        const mapping = skuMap[key]
        return Object.entries(mapping).map(([ sku, quantity ]) => {
          const product = productList.find(i => i.sku === sku)
          return {
            product,
            quantity
          }
        }).filter((i): i is VarietyPackEntry => Boolean(i.product)).reverse()
      // deprecated! use skuMap instead for easier sharing between prod & dev
      } else if (quantityMap?.[key]) {
        const mapping = quantityMap[key]
        return Object.entries(mapping).map(([ pid, quantity ]) => {
          const product = productList.find(i => i.key.startsWith(pid))
          return {
            product,
            quantity
          }
        }).filter((i): i is VarietyPackEntry => Boolean(i.product))
      }
    }
    return []
  }

  const handleVarietyPackRoute = async (handle: string) => {
    if (varietyPacks.value.length === 0) {
      await load()
    }
    varietyPack.value = varietyPacks.value.find(v => v.handle === handle)
  }

  const useVarietyPacks = async () => {
    if (!loading.value && varietyPacks.value.length === 0) {
      await load()
    }
    return varietyPacks
  }

  const reset = () => {
    varietyPacks.value = []
  }

  return {
    useVarietyPacks,
    handleVarietyPackRoute,
    loading,
    varietyPacks,
    varietyPack,
    contents,
    reset
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [
      'varietyPacks'
    ],
    afterHydrate: ({ store }) => {
      if (!store.varietyPacks?.[0]?.skuMap) {
        store.load()
      }
    }
  }
})
